import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import {
ArrowLeft, Video, Phone, FileText, Send, Paperclip, User, Loader2,
Heart, MapPin, Droplets, FileImage, Activity, ChevronDown, ChevronUp, X, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PrescriptionForm from "@/components/PrescriptionForm";
import { useWebRTC } from "@/hooks/useWebRTC";
import CallOverlay from "@/components/CallOverlay";
import { getConsultationAccess, type ConsultationAccess } from "@/utils/consultationAccess";
import { toast } from "sonner";

type PractitionerType = "dietitian" | "doctor";
interface ChatBoxProps { practitionerType: PractitionerType; }

const cleanPrescriptionText = (text: string) => text.replace(/^📋\s*/, "");

function getHealthStatus(bloodSugar: number | null) {
if (!bloodSugar) return { label: "Unknown", color: "text-muted-foreground", bg: "bg-muted/40" };
if (bloodSugar > 200) return { label: "High Risk", color: "text-destructive", bg: "bg-destructive/10" };
if (bloodSugar > 140) return { label: "Needs Monitoring", color: "text-warning", bg: "bg-warning/10" };
if (bloodSugar >= 70) return { label: "Stable", color: "text-success", bg: "bg-success/10" };
return { label: "Under Care", color: "text-primary", bg: "bg-primary/10" };
}

const ChatBox = ({ practitionerType }: ChatBoxProps) => {
const { conversationId: routeConversationId, patientId: legacyPatientId } = useParams<{ conversationId?: string; patientId?: string }>();
const navigate = useNavigate();
const { language } = useLanguage();
const { user } = useAuth();
const location = useLocation();
const bn = language === "bn";
const messagesEndRef = useRef<HTMLDivElement>(null);

const [patient, setPatient] = useState<any>(null);
const [healthRecords, setHealthRecords] = useState<any[]>([]);
const [messages, setMessages] = useState<any[]>([]);
const [newMessage, setNewMessage] = useState("");
const [loading, setLoading] = useState(true);
const [showPrescription, setShowPrescription] = useState(false);
const [showHealthData, setShowHealthData] = useState(false);
const [showRecords, setShowRecords] = useState(false);
const [conversationId, setConversationId] = useState<string | null>(null);
const [conversation, setConversation] = useState<any>(null);
const [loadError, setLoadError] = useState<string | null>(null);
const [, setAccessClock] = useState(0);
const prescriptionSendingRef = useRef(false);
const patientId = patient?.user_id ?? conversation?.patient_id ?? legacyPatientId;

useEffect(() => {
if (!user || (!routeConversationId && !legacyPatientId)) return;
const load = async () => {
setLoading(true);
setLoadError(null);
let conversationQuery = supabase.from("conversations").select("")
.eq("practitioner_id", user.id).eq("practitioner_type", practitionerType);
conversationQuery = routeConversationId
? conversationQuery.eq("id", routeConversationId)
: conversationQuery.eq("patient_id", legacyPatientId!);
const convRes = await conversationQuery.maybeSingle();
if (convRes.error || !convRes.data) {
setLoadError(convRes.error?.message ?? "Conversation not found");
setLoading(false);
return;
}
const conv = convRes.data;
const [patientRes, recordsRes, messagesRes] = await Promise.all([
supabase.from("profiles").select("").eq("user_id", conv.patient_id).single(),
supabase.from("health_records")
.select("id, report_name, file_type, file_data, created_at")
.eq("patient_id", conv.patient_id)
.order("created_at", { ascending: false }),
supabase.from("messages").select("*").eq("conversation_id", conv.id).order("created_at"),
]);
const firstError = patientRes.error ?? messagesRes.error;
if (firstError) {
setLoadError(firstError.message);
setLoading(false);
return;
}
if (recordsRes.error) {
toast.error(bn ? "স্বাস্থ্য রেকর্ড লোড করা যায়নি" : "Health records could not be loaded");
}
setPatient(patientRes.data);
setHealthRecords(recordsRes.data ?? []);
setConversation(conv);
setConversationId(conv.id);
setMessages(messagesRes.data ?? []);
setLoading(false);
};
load();
}, [routeConversationId, legacyPatientId, user, practitionerType]);

useEffect(() => {
messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
}, [messages]);

useEffect(() => {
if (!conversation) return;
const timer = window.setInterval(() => setAccessClock((value) => value + 1), 30_000);
return () => window.clearInterval(timer);
}, [conversation]);

const sendMessage = async () => {
if (!newMessage.trim() || !user) return;
if (!conversation || getConsultationAccess(conversation) !== "open") {
toast.error(
getConsultationAccess(conversation ?? { status: "pending", paid: false }) === "expired"
? (bn ? "এই পরামর্শের ১৫ দিন শেষ হয়েছে" : "This 15-day consultation has expired")
: (bn ? "চ্যাট বর্তমানে বন্ধ আছে" : "Chat is currently locked"),
);
return;
}
const cId = conversationId;
if (!cId) return;
const { data: msg, error } = await supabase.rpc("send_consultation_message", {
_conversation_id: cId,
_text: newMessage.trim(),
_message_type: "text",
});
if (error) {
toast.error(error.message);
return;
}
if (msg) setMessages(prev => [...prev, msg]);
setNewMessage("");
};

const { callState, callType, isMuted, isCameraOff, isSpeakerOn, incomingCall, localStream, remoteStream,
startCall, answerCall, rejectCall, endCall, toggleMute, toggleCamera, toggleSpeaker } = useWebRTC({
conversationId, userId: user?.id ?? null,
userName: user?.name ?? (bn ? "চিকিৎসক" : "Practitioner"),
});

// GlobalCallListener routes accepted calls into this chat with autoAnswer.
// Wait for the shared WebRTC listener to receive the repeated call request,
// then answer only once the conversation is loaded.
useEffect(() => {
const state = location.state as { autoAnswer?: boolean } | null;
if (!state?.autoAnswer || callState !== "incoming" || !incomingCall) return;
void answerCall();
navigate(location.pathname, { replace: true, state: null });
}, [location.pathname, location.state, callState, incomingCall, answerCall, navigate]);

const handlePrescriptionSend = async (prescription: {
sections: any[]; specialNotes: string; tasks: string[]; durationDays: number
}) => {
if (!user || !patientId) return;
if (!conversation || getConsultationAccess(conversation) !== "open") {
toast.error(bn ? "চ্যাট বর্তমানে বন্ধ আছে" : "Chat is currently locked");
return;
}
if (prescriptionSendingRef.current) return;
prescriptionSendingRef.current = true;
const cId = conversationId;
try {
if (!cId) return;
const { error: prescriptionError } = await supabase.from("prescriptions").insert({
patient_id: patientId, practitioner_id: user.id,
practitioner_type: practitionerType, sections: prescription.sections,
special_notes: prescription.specialNotes, tasks: prescription.tasks,
duration_days: prescription.durationDays,
});
if (prescriptionError) throw prescriptionError;
for (const taskText of prescription.tasks) {
const { error: taskError } = await supabase.from("tasks").insert({
patient_id: patientId, assigned_by: user.id,
text: taskText, days_remaining: prescription.durationDays,
});
if (taskError) throw taskError;
}
if (cId) {
const text = bn
  ? `প্রেসক্রিপশন পাঠানো হয়েছে (${prescription.durationDays} দিন)`
  : `Prescription sent (${prescription.durationDays} days)`;
const { data: msg, error } = await supabase.rpc("send_consultation_message", {
_conversation_id: cId,
_text: text,
_message_type: "prescription",
});
if (error) throw error;
if (msg) setMessages(prev => [...prev, msg]);
}
setShowPrescription(false);
toast.success(bn ? "প্রেসক্রিপশন পাঠানো হয়েছে" : "Prescription sent");
} catch (error: any) {
toast.error(error?.message ?? (bn ? "প্রেসক্রিপশন পাঠানো যায়নি" : "Could not send prescription"));
} finally {
prescriptionSendingRef.current = false;
}
};

if (loading) {
return (
<div className="flex items-center justify-center h-full">
<Loader2 className="h-8 w-8 animate-spin text-primary" />
</div>
);
}

if (loadError || !conversation) {
return (
<div className="flex h-full items-center justify-center p-6">
<div className="glass max-w-sm rounded-2xl p-6 text-center shadow-float">
<p className="font-bold text-destructive">{bn ? "কথোপকথন খোলা যায়নি" : "Could not open conversation"}</p>
<p className="mt-2 text-xs text-muted-foreground">{loadError ?? (bn ? "কথোপকথন পাওয়া যায়নি" : "Conversation not found")}</p>
<Button className="mt-4" onClick={() => navigate(-1)}>{bn ? "ফিরে যান" : "Go back"}</Button>
</div>
</div>
);
}

const healthStatus = getHealthStatus(patient?.blood_sugar ?? null);
const consultationAccess: ConsultationAccess = getConsultationAccess(
conversation ?? { status: "pending", paid: false },
);
const liveChatOpen = consultationAccess === "open";

return (
<>
{/* ── Main chat layout: fixed to viewport exactly like PatientInbox ── */}
<div className="fixed inset-0 z-[60] flex flex-col bg-[#f2faf9] md:left-64">

{/* Header */}  
    <div className="flex shrink-0 items-center gap-2 border-b border-[#e2ebe7] bg-white px-4 py-3">  
      <button onClick={() => navigate(-1)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eef7f4] text-[#367f78]">  
        <ArrowLeft className="h-5 w-5" />  
      </button>  

      <div className="flex items-center gap-2 flex-1 min-w-0">  
        {patient?.profile_photo ? (  
          <img src={patient.profile_photo} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />  
        ) : (  
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e1f2ed]">  
            <User className="h-5 w-5 text-[#398b82]" />  
          </div>  
        )}  
        <div className="min-w-0">  
          <p className="truncate text-sm font-extrabold leading-tight text-[#244c47]">{patient?.full_name ?? (bn ? "রোগী" : "Patient")}</p>  
          <p className="text-[10px] leading-tight text-[#788783]">  
            {patient?.age ? `${patient.age}${bn ? " বছর" : "y"} ` : ""}{patient?.gender ?? ""}  
          </p>  
        </div>  
      </div>  

      <div className="flex items-center gap-1 shrink-0">  
        <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-[#398b82] hover:bg-[#e1f2ed]"  
          title={bn ? "রোগীর তথ্য" : "Patient Data"}  
          onClick={() => setShowHealthData(true)}>  
          <Info className="h-4 w-4" />  
        </Button>  
        <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-[#398b82] hover:bg-[#e1f2ed]"  
          onClick={() => startCall("audio")} disabled={callState !== "idle" || !liveChatOpen}>  
          <Phone className="h-4 w-4" />  
        </Button>  
        <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-[#398b82] hover:bg-[#e1f2ed]"  
          onClick={() => startCall("video")} disabled={callState !== "idle" || !liveChatOpen}>  
          <Video className="h-4 w-4" />  
        </Button>  
        <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-[#398b82] hover:bg-[#e1f2ed]"  
          title={bn ? "প্রেসক্রিপশন" : "Prescription"}  
          onClick={() => setShowPrescription(true)} disabled={!liveChatOpen}>  
          <FileText className="h-4 w-4" />  
        </Button>  
      </div>  
    </div>  

    {/* Messages */}  
    <div className="flex-1 space-y-3 overflow-y-auto bg-[#f2faf9] px-5 py-4">  
      {messages.length === 0 && (  
        <div className="flex items-center justify-center h-full min-h-[200px]">  
          <div className="text-center space-y-2">  
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#e1f2ed]">  
              <Send className="h-7 w-7 text-[#82bdb5]" />  
            </div>  
            <p className="text-sm text-muted-foreground">{bn ? "কথোপকথন শুরু করুন" : "Start a conversation"}</p>  
          </div>  
        </div>  
      )}  
      {messages.map((msg) => {  
        const isPractitioner = msg.sender_id === user?.id;  
        return (  
          <motion.div key={msg.id} className={`flex ${isPractitioner ? "justify-end" : "justify-start"}`}  
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>  
            <div className={`max-w-[78%] rounded-[23px] border px-4 py-3 text-sm ${  
              isPractitioner  
                ? "rounded-tr-[8px] border-[#d5e7e2] bg-[#e7f4f1] text-[#244c47]"  
                : "rounded-tl-[8px] border-[#e1ebe8] bg-white text-[#244c47]"  
            }`}>  
              {msg.message_type === "prescription" && (  
                <div className="mb-1 flex items-center gap-2 text-[#398b82]">  
                  <FileText className="h-4 w-4" />  
                  <span className="sr-only">{bn ? "প্রেসক্রিপশন" : "Prescription"}</span>  
                </div>  
              )}  
              <p>{msg.message_type === "prescription" ? cleanPrescriptionText(msg.text) : msg.text}</p>  
              <p className="mt-1 text-[9px] text-[#788783]">  
                {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}  
              </p>  
            </div>  
          </motion.div>  
        );  
      })}  
      <div ref={messagesEndRef} />  
    </div>  

    {/* Input bar */}  
    <div className="shrink-0 border-t border-[#e2ebe7] bg-white px-4 py-3">  
      {!liveChatOpen && (  
        <div className="mb-2 rounded-xl bg-warning/10 px-3 py-2 text-xs font-semibold text-warning">  
          {consultationAccess === "expired"  
            ? (bn ? "১৫ দিনের পরামর্শ শেষ হয়েছে" : "This 15-day consultation has expired")  
            : (bn ? "চ্যাট ২৪ ঘণ্টার নিষ্ক্রিয়তার পর বন্ধ হয়েছে" : "Chat is locked after 24 hours of inactivity")}  
        </div>  
      )}  
      <div className="flex items-center gap-2">  
        <span className="flex h-11 w-9 items-center justify-center text-[#398b82]" aria-hidden="true">  
          <Paperclip className="h-5 w-5" />  
        </span>  
        <input  
          type="text" value={newMessage}  
          onChange={(e) => setNewMessage(e.target.value)}  
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}  
          placeholder={bn ? "বার্তা লিখুন..." : "Type a message..."}  
          disabled={!liveChatOpen}  
          className="flex-1 rounded-full border border-[#e1ebe8] bg-[#f7fbfa] px-4 py-3 text-sm outline-none placeholder:text-[#8a9693] focus:border-[#6aafa5] focus:ring-0"  
        />  
        <Button onClick={sendMessage} disabled={!newMessage.trim() || !liveChatOpen}  
          className="h-12 w-12 shrink-0 rounded-full bg-[#63aaa1] text-white hover:bg-[#4d988e]" size="icon">  
          <Send className="h-4 w-4" />  
        </Button>  
      </div>  
    </div>  
  </div>  

  {/* ── Patient Health Data — Bottom Sheet Modal ── */}  
  {createPortal(  
  <AnimatePresence>  
    {showHealthData && (  
      <motion.div  
        className="fixed inset-0 z-[70] flex flex-col justify-end"  
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}  
      >  
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowHealthData(false)} />  
        <motion.div  
          className="relative bg-card rounded-t-3xl max-h-[85vh] flex flex-col shadow-2xl"  
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}  
          transition={{ type: "spring", damping: 28, stiffness: 300 }}  
        >  
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border/40 shrink-0">  
            <div className="flex items-center gap-2">  
              <Activity className="h-4 w-4 text-primary" />  
              <h3 className="font-extrabold text-sm">{bn ? "রোগীর স্বাস্থ্য তথ্য" : "Patient Health Data"}</h3>  
            </div>  
            <button onClick={() => setShowHealthData(false)} className="p-1.5 rounded-xl hover:bg-muted/60">  
              <X className="h-4 w-4 text-muted-foreground" />  
            </button>  
          </div>  

          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">  
            {/* Photo + name */}  
            <div className="flex items-center gap-4">  
              {patient?.profile_photo ? (  
                <img src={patient.profile_photo} alt="" className="h-16 w-16 rounded-2xl object-cover shrink-0" />  
              ) : (  
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#e4f1eb]">  
                  <User className="h-8 w-8 text-primary" />  
                </div>  
              )}  
              <div>  
                <p className="font-extrabold text-base">{patient?.full_name}</p>  
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${healthStatus.bg} ${healthStatus.color} inline-block mt-1`}>  
                  {bn  
                    ? healthStatus.label === "Stable" ? "স্থিতিশীল"  
                      : healthStatus.label === "Needs Monitoring" ? "পর্যবেক্ষণ দরকার"  
                      : healthStatus.label === "High Risk" ? "উচ্চ ঝুঁকি"  
                      : healthStatus.label === "Under Care" ? "চিকিৎসাধীন"  
                      : "অজানা"  
                    : healthStatus.label}  
                </span>  
              </div>  
            </div>  

            {/* Stats grid */}  
            <div className="grid grid-cols-2 gap-2">  
              {[  
                { label: bn ? "বয়স" : "Age", value: patient?.age ? `${patient.age} ${bn ? "বছর" : "yrs"}` : "—" },  
                { label: bn ? "লিঙ্গ" : "Sex", value: patient?.gender || "—" },  
                { label: bn ? "ফোন" : "Phone", value: patient?.phone || "—" },  
                { label: bn ? "রক্তের শর্করা" : "Blood Glucose", value: patient?.blood_sugar ? `${patient.blood_sugar} mg/dL` : "—" },  
              ].map(stat => (  
                <div key={stat.label} className="bg-muted/30 rounded-xl p-3">  
                  <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">{stat.label}</p>  
                  <p className="text-xs font-bold mt-0.5 truncate">{stat.value}</p>  
                </div>  
              ))}  
            </div>  

            {/* Blood sugar bar */}  
            {(patient?.blood_sugar ?? 0) > 0 && (  
              <div className="flex items-center gap-3">  
                <Droplets className="h-4 w-4 text-primary shrink-0" />  
                <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">  
                  <div  
                    className={`h-full rounded-full ${patient.blood_sugar > 200 ? "bg-destructive" : patient.blood_sugar > 140 ? "bg-warning" : "bg-success"}`}  
                    style={{ width: `${Math.min(100, (patient.blood_sugar / 300) * 100)}%` }}  
                  />  
                </div>  
                <span className="text-[10px] font-bold text-muted-foreground w-20 text-right">{patient.blood_sugar} mg/dL</span>  
              </div>  
            )}  

            {/* Health condition */}  
            {patient?.health_profile && (  
              <div className="bg-primary/5 rounded-xl p-3">  
                <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">  
                  <Heart className="h-3 w-3 text-primary" />{bn ? "স্বাস্থ্য সমস্যা" : "Health Condition"}  
                </p>  
                <p className="text-xs font-medium">{patient.health_profile}</p>  
              </div>  
            )}  

            {/* Address */}  
            {patient?.address && (  
              <div className="flex items-start gap-2 text-xs text-muted-foreground">  
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />  
                <span>{patient.address}</span>  
              </div>  
            )}  

            {/* Health records */}  
            <div>  
              <button  
                onClick={() => setShowRecords(v => !v)}  
                className="w-full flex items-center justify-between text-[10px] font-bold text-muted-foreground uppercase tracking-wider bg-muted/30 rounded-xl px-3 py-2.5 hover:bg-muted/50 transition-colors"  
              >  
                <span className="flex items-center gap-1.5">  
                  <FileImage className="h-3 w-3 text-primary" />  
                  {bn ? "আপলোড করা রিপোর্ট" : "Uploaded Reports"}  
                  <span className="bg-primary text-primary-foreground text-[9px] font-extrabold px-1.5 py-0.5 rounded-full">  
                    {healthRecords.length}  
                  </span>  
                </span>  
                {showRecords ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}  
              </button>  
              <AnimatePresence>  
                {showRecords && (  
                  <motion.div  
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}  
                    className="overflow-hidden"  
                  >  
                    {healthRecords.length === 0 ? (  
                      <p className="text-[10px] text-muted-foreground text-center py-3">{bn ? "কোনো রিপোর্ট নেই" : "No reports uploaded"}</p>  
                    ) : (  
                      <div className="mt-2 space-y-1.5">  
                        {healthRecords.map(rec => (  
                          <div key={rec.id} className="flex items-center gap-2 bg-background rounded-xl p-2.5">  
                            {rec.file_type === "image" ? (  
                              <img src={rec.file_data} alt={rec.report_name} className="h-10 w-10 rounded-lg object-cover shrink-0 border border-border/30" />  
                            ) : (  
                              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">  
                                <FileText className="h-5 w-5 text-primary" />  
                              </div>  
                            )}  
                            <div className="flex-1 min-w-0">  
                              <p className="text-[11px] font-bold truncate">{rec.report_name}</p>  
                              <p className="text-[9px] text-muted-foreground">{new Date(rec.created_at).toLocaleDateString()}</p>  
                            </div>  
                          </div>  
                        ))}  
                      </div>  
                    )}  
                  </motion.div>  
                )}  
              </AnimatePresence>  
            </div>  

            {/* Latest status footer */}  
            <div className="flex items-center gap-2 pb-2">  
              <Activity className="h-3.5 w-3.5 text-muted-foreground shrink-0" />  
              <span className="text-[10px] font-bold text-muted-foreground">{bn ? "সর্বশেষ স্বাস্থ্য অবস্থা:" : "Latest Health Status:"}</span>  
              <span className={`text-[10px] font-extrabold ${healthStatus.color}`}>  
                {bn  
                  ? healthStatus.label === "Stable" ? "স্থিতিশীল"  
                    : healthStatus.label === "Needs Monitoring" ? "পর্যবেক্ষণ দরকার"  
                    : healthStatus.label === "High Risk" ? "উচ্চ ঝুঁকি"  
                    : healthStatus.label === "Under Care" ? "চিকিৎসাধীন"  
                    : "অজানা"  
                  : healthStatus.label}  
              </span>  
            </div>  
          </div>  
        </motion.div>  
      </motion.div>  
    )}  
  </AnimatePresence>  
  , document.body)}  

  {/* ── Prescription — Bottom Sheet Modal ── */}  
  {createPortal(  
  <AnimatePresence>  
    {showPrescription && (  
      <motion.div  
        className="fixed inset-0 z-[70] flex flex-col justify-end"  
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}  
      >  
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowPrescription(false)} />  
        <motion.div  
          className="relative bg-card rounded-t-3xl max-h-[92vh] flex flex-col shadow-2xl"  
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}  
          transition={{ type: "spring", damping: 28, stiffness: 300 }}  
        >  
          <div className="overflow-y-auto flex-1 px-4 pt-4 pb-safe">  
            <PrescriptionForm  
              practitionerType={practitionerType}  
              patientName={patient?.full_name ?? ""}  
              practitionerName={user?.name ?? ""}  
              onSend={handlePrescriptionSend}  
              onClose={() => setShowPrescription(false)}  
            />  
          </div>  
        </motion.div>  
      </motion.div>  
    )}  
  </AnimatePresence>  
  , document.body)}  

  <CallOverlay  
    callState={callState} callType={callType} partnerName={patient?.full_name ?? ""}  
    isMuted={isMuted} isCameraOff={isCameraOff} isSpeakerOn={isSpeakerOn}  
    incomingCall={incomingCall} localStream={localStream} remoteStream={remoteStream}  
    onAnswer={answerCall} onReject={rejectCall} onEnd={endCall}  
    onToggleMute={toggleMute} onToggleCamera={toggleCamera} onToggleSpeaker={toggleSpeaker}  
  />  
</>

);
};

export default ChatBox;